/**
 * Avatar art: each facial/clothing feature is a small SVG-fragment generator,
 * drawn flat-cartoon style in a 200×200 viewBox. The head is centred near
 * (100, 96); shoulders sit at the bottom edge (upper-body portrait only).
 *
 * Every generator returns an SVG markup string; `index.ts` stacks them in the
 * right z-order. Counts here drive the editor's "next/prev" ranges, so adding an
 * option is just adding an entry to the matching array.
 */

// ---- palettes (indices are what AvatarConfig stores) ----
export const SKIN = ['#ffd9b8', '#f3c19a', '#e0a878', '#c8895a', '#a26840', '#7c4a2d', '#5a3420'];
export const HAIR = ['#1f1a17', '#3b2a1e', '#6b4423', '#a9733b', '#d8b36a', '#e8e2d6', '#b23b2e', '#4b5566', '#7d5fa3'];
export const CLOTH = ['#3b6ea5', '#c0392b', '#27ae60', '#2c3e50', '#e67e22', '#8e44ad', '#16a085', '#34495e', '#d65d8f'];
export const BG = ['#2a3340', '#3a4a5a', '#4a3a52', '#54483a', '#3a4a42', '#444', '#2d2d3a', '#503a3a'];

const CX = 100;

/** Slightly darken a hex colour (for shading), by `f` (0..1). */
function shade(hex: string, f = 0.22): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.round(((n >> 16) & 255) * (1 - f));
  const g = Math.round(((n >> 8) & 255) * (1 - f));
  const b = Math.round((n & 255) * (1 - f));
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

// ---- background ----
export function background(i: number): string {
  return `<rect x="0" y="0" width="200" height="200" rx="20" fill="${BG[i % BG.length]}"/>`;
}

// ---- clothing (shoulders + neckline), 6 options ----
export const CLOTHING_COUNT = 6;
export function clothing(i: number, colorIdx: number): string {
  const c = CLOTH[colorIdx % CLOTH.length];
  const d = shade(c);
  const shoulders = `<path d="M22 200 C26 168 54 150 100 150 C146 150 174 168 178 200 Z" fill="${c}"/>`;
  switch (i % CLOTHING_COUNT) {
    case 0: // crew tee
      return shoulders + `<path d="M82 152 Q100 166 118 152 L114 150 Q100 160 86 150 Z" fill="${d}"/>`;
    case 1: // hoodie
      return (
        shoulders +
        `<path d="M78 151 Q100 176 122 151 L118 150 Q100 168 82 150 Z" fill="${d}"/>` +
        `<rect x="96" y="158" width="3" height="34" fill="${d}"/><rect x="101" y="158" width="3" height="34" fill="${d}"/>`
      );
    case 2: // collared shirt
      return (
        shoulders +
        `<path d="M84 150 L100 170 L116 150 L108 148 L100 158 L92 148 Z" fill="#f2efe6"/>` +
        `<path d="M100 158 L96 200 L104 200 Z" fill="${d}"/>`
      );
    case 3: // suit + tie
      return (
        shoulders +
        `<path d="M80 150 L100 200 L120 150 L110 150 L100 168 L90 150 Z" fill="#f2efe6"/>` +
        `<path d="M100 156 L94 150 L106 150 Z M100 162 L95 196 L105 196 Z" fill="${CLOTH[1]}"/>` +
        `<path d="M78 152 L70 200 L86 200 L100 170 Z M122 152 L130 200 L114 200 L100 170 Z" fill="${d}"/>`
      );
    case 4: // tank top
      return `<path d="M30 200 C34 172 60 156 100 156 C140 156 166 172 170 200 Z" fill="${c}"/>
        <path d="M80 156 Q100 150 120 156 L116 150 Q100 156 84 150 Z" fill="${d}"/>
        <rect x="74" y="150" width="7" height="44" fill="${c}"/><rect x="119" y="150" width="7" height="44" fill="${c}"/>`;
    default: // turtleneck sweater
      return (
        shoulders +
        `<path d="M84 150 Q100 144 116 150 L116 158 Q100 152 84 158 Z" fill="${d}"/>` +
        `<path d="M86 138 Q100 132 114 138 L114 150 Q100 144 86 150 Z" fill="${c}"/>`
      );
  }
}

// ---- neck (always drawn under the head) ----
export function neck(skinIdx: number): string {
  const s = SKIN[skinIdx % SKIN.length];
  return `<path d="M88 126 L88 152 Q100 160 112 152 L112 126 Z" fill="${shade(s, 0.12)}"/>`;
}

// ---- face shape (head + ears), 4 options ----
export const FACE_COUNT = 4;
export function face(i: number, skinIdx: number): string {
  const s = SKIN[skinIdx % SKIN.length];
  const ear = `<ellipse cx="52" cy="98" rx="9" ry="13" fill="${s}"/><ellipse cx="148" cy="98" rx="9" ry="13" fill="${s}"/>`;
  let head: string;
  switch (i % FACE_COUNT) {
    case 0: // oval
      head = `<path d="M100 44 C70 44 58 70 58 96 C58 126 78 148 100 148 C122 148 142 126 142 96 C142 70 130 44 100 44 Z" fill="${s}"/>`;
      break;
    case 1: // round
      head = `<ellipse cx="100" cy="96" rx="46" ry="48" fill="${s}"/>`;
      break;
    case 2: // square (strong jaw)
      head = `<path d="M60 70 Q60 46 100 46 Q140 46 140 70 L140 116 Q140 146 100 148 Q60 146 60 116 Z" fill="${s}"/>`;
      break;
    default: // heart / pointed chin
      head = `<path d="M60 72 Q62 46 100 46 Q138 46 140 72 C140 104 122 150 100 150 C78 150 60 104 60 72 Z" fill="${s}"/>`;
  }
  return ear + head;
}

// ---- eyebrows, 4 options (mirrored pair) ----
export const BROW_COUNT = 4;
export function brows(i: number, hairIdx: number): string {
  const c = shade(HAIR[hairIdx % HAIR.length], 0.1);
  const one = (x: number, flip: number): string => {
    switch (i % BROW_COUNT) {
      case 0: // straight
        return `<rect x="${x - 12}" y="80" width="24" height="4" rx="2" fill="${c}" transform="scale(${flip},1) translate(${flip < 0 ? -200 : 0},0)"/>`;
      case 1: // raised arch
        return `<path d="M${x - 12} 82 Q${x} 73 ${x + 12} 80" stroke="${c}" stroke-width="4" fill="none" stroke-linecap="round" transform="scale(${flip},1) translate(${flip < 0 ? -200 : 0},0)"/>`;
      case 2: // angry / slanted
        return `<path d="M${x - 12} 78 L${x + 12} 84" stroke="${c}" stroke-width="4" fill="none" stroke-linecap="round" transform="scale(${flip},1) translate(${flip < 0 ? -200 : 0},0)"/>`;
      default: // soft worried
        return `<path d="M${x - 12} 80 Q${x} 84 ${x + 12} 79" stroke="${c}" stroke-width="4" fill="none" stroke-linecap="round" transform="scale(${flip},1) translate(${flip < 0 ? -200 : 0},0)"/>`;
    }
  };
  return one(78, 1) + one(78, -1);
}

// ---- eyes, 5 options (mirrored pair) ----
export const EYE_COUNT = 5;
export function eyes(i: number): string {
  const lx = 80;
  const rx = 120;
  const y = 96;
  const opt = i % EYE_COUNT;
  const draw = (x: number): string => {
    switch (opt) {
      case 0: // round
        return `<circle cx="${x}" cy="${y}" r="8" fill="#fff"/><circle cx="${x}" cy="${y}" r="4.2" fill="#3a2a20"/><circle cx="${x + 1.5}" cy="${y - 1.5}" r="1.3" fill="#fff"/>`;
      case 1: // almond
        return `<path d="M${x - 9} ${y} Q${x} ${y - 7} ${x + 9} ${y} Q${x} ${y + 6} ${x - 9} ${y} Z" fill="#fff"/><circle cx="${x}" cy="${y}" r="3.6" fill="#3a2a20"/>`;
      case 2: // wide
        return `<ellipse cx="${x}" cy="${y}" rx="9" ry="9.5" fill="#fff"/><circle cx="${x}" cy="${y + 0.5}" r="4.6" fill="#2b3a55"/><circle cx="${x + 1.6}" cy="${y - 1.6}" r="1.4" fill="#fff"/>`;
      case 3: // sleepy / half
        return `<path d="M${x - 9} ${y} Q${x} ${y - 4} ${x + 9} ${y}" stroke="#fff" stroke-width="0"/><path d="M${x - 8} ${y - 1} Q${x} ${y + 4} ${x + 8} ${y - 1}" fill="#fff"/><circle cx="${x}" cy="${y + 1}" r="3.4" fill="#3a2a20"/><path d="M${x - 9} ${y - 2} Q${x} ${y - 5} ${x + 9} ${y - 2}" stroke="#5a4636" stroke-width="2" fill="none" stroke-linecap="round"/>`;
      default: // happy closed (^ ^)
        return `<path d="M${x - 8} ${y + 2} Q${x} ${y - 6} ${x + 8} ${y + 2}" stroke="#3a2a20" stroke-width="3" fill="none" stroke-linecap="round"/>`;
    }
  };
  return draw(lx) + draw(rx);
}

// ---- nose, 4 options ----
export const NOSE_COUNT = 4;
export function nose(i: number, skinIdx: number): string {
  const c = shade(SKIN[skinIdx % SKIN.length], 0.2);
  switch (i % NOSE_COUNT) {
    case 0: // soft tip
      return `<path d="M100 104 Q96 116 100 117 Q104 116 100 104" fill="none" stroke="${c}" stroke-width="3" stroke-linecap="round"/>`;
    case 1: // button
      return `<ellipse cx="100" cy="114" rx="5" ry="4" fill="${c}"/>`;
    case 2: // long ridge
      return `<path d="M100 102 L97 116 Q100 119 103 116" fill="none" stroke="${c}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>`;
    default: // wide nostrils
      return `<path d="M95 115 Q92 118 96 118 M105 115 Q108 118 104 118" stroke="${c}" stroke-width="3" fill="none" stroke-linecap="round"/>`;
  }
}

// ---- mouth, 5 options ----
export const MOUTH_COUNT = 5;
export function mouth(i: number): string {
  const y = 128;
  switch (i % MOUTH_COUNT) {
    case 0: // smile
      return `<path d="M88 ${y} Q100 ${y + 10} 112 ${y}" stroke="#7c3b32" stroke-width="3.5" fill="none" stroke-linecap="round"/>`;
    case 1: // neutral
      return `<path d="M90 ${y + 3} L110 ${y + 3}" stroke="#7c3b32" stroke-width="3.5" fill="none" stroke-linecap="round"/>`;
    case 2: // open grin (teeth)
      return `<path d="M88 ${y} Q100 ${y + 14} 112 ${y} Z" fill="#7c3b32"/><path d="M90 ${y + 1} Q100 ${y + 5} 110 ${y + 1} Z" fill="#fff"/>`;
    case 3: // smirk
      return `<path d="M90 ${y + 4} Q102 ${y + 2} 112 ${y - 3}" stroke="#7c3b32" stroke-width="3.5" fill="none" stroke-linecap="round"/>`;
    default: // small / shy
      return `<path d="M94 ${y + 2} Q100 ${y + 7} 106 ${y + 2}" stroke="#7c3b32" stroke-width="3.5" fill="none" stroke-linecap="round"/>`;
  }
}

// ---- hair, 8 options. Returns { back, front } so long styles sit behind head. ----
export const HAIR_COUNT = 8;
export function hair(i: number, colorIdx: number): { back: string; front: string } {
  const c = HAIR[colorIdx % HAIR.length];
  const d = shade(c);
  const none = { back: '', front: '' };
  switch (i % HAIR_COUNT) {
    case 0: // bald / none
      return none;
    case 1: // short crop
      return {
        back: '',
        front: `<path d="M56 92 Q52 50 100 48 Q148 50 144 92 Q140 64 100 62 Q60 64 56 92 Z" fill="${c}"/>`,
      };
    case 2: // side part
      return {
        back: '',
        front: `<path d="M56 96 Q54 50 100 48 Q150 50 146 96 Q146 70 112 64 Q108 78 92 70 Q70 64 56 96 Z" fill="${c}"/>`,
      };
    case 3: // spiky
      return {
        back: '',
        front: `<path d="M58 94 L64 60 L74 78 L84 54 L94 76 L100 52 L108 76 L118 54 L128 78 L138 60 L142 94 Q138 66 100 64 Q62 66 58 94 Z" fill="${c}"/>`,
      };
    case 4: // curly afro
      return {
        back: `<circle cx="100" cy="74" r="50" fill="${c}"/>`,
        front: `<g fill="${d}"><circle cx="62" cy="66" r="13"/><circle cx="84" cy="52" r="14"/><circle cx="108" cy="50" r="14"/><circle cx="134" cy="60" r="13"/><circle cx="142" cy="82" r="11"/><circle cx="58" cy="86" r="11"/></g>`,
      };
    case 5: // long straight
      return {
        back: `<path d="M52 96 Q48 50 100 46 Q152 50 148 96 L150 168 Q150 176 138 176 L132 110 Q120 70 100 68 Q80 70 68 110 L62 176 Q50 176 50 168 Z" fill="${c}"/>`,
        front: `<path d="M56 94 Q54 52 100 50 Q146 52 144 94 Q140 66 100 64 Q60 66 56 94 Z" fill="${d}"/>`,
      };
    case 6: // ponytail
      return {
        back: `<path d="M138 78 Q164 86 160 120 Q156 140 146 138 Q156 120 150 100 Q146 86 134 86 Z" fill="${c}"/>`,
        front: `<path d="M56 94 Q52 50 100 48 Q148 50 144 94 Q140 64 100 62 Q60 64 56 94 Z" fill="${c}"/>`,
      };
    default: // top bun
      return {
        back: '',
        front: `<circle cx="100" cy="44" r="13" fill="${c}"/><path d="M58 92 Q54 54 100 52 Q146 54 142 92 Q138 66 100 64 Q62 66 58 92 Z" fill="${c}"/>`,
      };
  }
}
